import { useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { X, KeyRound, Copy, Eye, EyeOff, Sparkles } from "lucide-react";

function generatePwd(len = 14) {
  const alpha = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const symb = "!@#$%^&*";
  let out = "";
  for (let i = 0; i < len - 2; i += 1) out += alpha[Math.floor(Math.random() * alpha.length)];
  out += symb[Math.floor(Math.random() * symb.length)];
  out += Math.floor(Math.random() * 10);
  return out.split("").sort(() => Math.random() - 0.5).join("");
}

export default function SetPasswordModal({ user, onClose }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(true);
  const [busy, setBusy] = useState(false);

  const strong = pwd.length >= 8;

  const save = async () => {
    if (!strong) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await adminApi.post(`/admin/user-accounts/${user.id}/set-password`, { new_password: pwd });
      toast.success(`Password updated for ${user.email}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set password");
    } finally { setBusy(false); }
  };
  const copy = () => {
    if (!pwd) return;
    navigator.clipboard?.writeText(pwd);
    toast.success("Copied to clipboard");
  };
  const gen = () => setPwd(generatePwd());

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="set-password-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center"><KeyRound size={16} /></div>
            <div>
              <h3 className="font-black text-slate-900">Set new password</h3>
              <p className="text-xs text-slate-500 truncate max-w-[280px]">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center"><X size={15} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            You&apos;ll need to send this password to the customer over chat/WhatsApp. They should change it after first sign-in.
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">New password</label>
            <div className="mt-1.5 flex gap-2">
              <div className="relative flex-1">
                <input type={show ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Minimum 8 characters"
                  data-testid="password-input" autoFocus
                  className="w-full h-11 pl-3 pr-10 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-indigo-400" />
                <button onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-800">
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <button onClick={gen} data-testid="password-generate" title="Generate strong password"
                className="inline-flex items-center gap-1.5 px-3 h-11 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">
                <Sparkles size={13} /> Generate
              </button>
              <button onClick={copy} disabled={!pwd} data-testid="password-copy" title="Copy"
                className="inline-flex items-center gap-1.5 px-3 h-11 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700 disabled:opacity-50">
                <Copy size={13} />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <div className={`h-1 flex-1 rounded-full ${pwd.length >= 12 ? "bg-emerald-500" : pwd.length >= 8 ? "bg-amber-400" : pwd.length > 0 ? "bg-rose-400" : "bg-slate-200"}`} />
              <span className="text-slate-500 font-bold tabular-nums w-16 text-right">
                {pwd.length === 0 ? "—" : pwd.length < 8 ? "weak" : pwd.length < 12 ? "ok" : "strong"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 h-10 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy || !strong} data-testid="password-save"
            className="px-5 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white text-sm font-black disabled:opacity-50">
            {busy ? "Saving…" : "Update password"}
          </button>
        </div>
      </div>
    </div>
  );
}
